import React from 'react'
import styled, { css } from 'styled-components'

import { ArrowRightIcon, useHapticFeedback } from '@blade/ui-kit'
import { mediaMinTablet } from '@root/shared/ui-kit'

type SettingsItemProps = {
  icon?: React.ReactNode
  title: React.ReactNode
  onClick?: () => void
  endContent?: React.ReactNode
  disabled?: boolean
}

export const SettingsItem: React.FC<SettingsItemProps> = ({
  icon,
  title,
  onClick,
  endContent,
  disabled = false,
}) => {
  const haptics = useHapticFeedback()
  const renderIcon = () => {
    if (icon) {
      return <Icon>{icon}</Icon>
    }
  }

  return (
    <Root
      onClick={() => {
        if (disabled || !onClick) {
          return
        }
        haptics.impactOccurred('light')
        onClick()
      }}
      aria-disabled={disabled}
      $cursorDefault={!!endContent && !onClick}
      $hasIcon={!!icon}
    >
      {renderIcon()}
      <Title>{title}</Title>
      {endContent ? endContent : <ArrowIcon size={24} />}
    </Root>
  )
}

const Root = styled.div<{ $cursorDefault?: boolean; $hasIcon?: boolean }>`
  cursor: ${({ $cursorDefault }) => ($cursorDefault ? 'default' : 'pointer')};
  justify-items: ${({ $hasIcon }) => ($hasIcon ? 'start' : 'end')};

  border-radius: 12px;
  height: 50px;
  padding-inline: 14px;

  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;

  gap: 8px;

  > :nth-child(1) {
    justify-self: center;
  }
  &:last-of-type {
    border-bottom: 0;
  }

  &[aria-disabled='true'] {
    cursor: default;
    background: var(--appearanceA-3);

    &:hover {
      background: var(--appearanceA-3);
    }
  }
  &[aria-disabled='false'] {
    background: var(--background-2);

    ${mediaMinTablet(css`
      &:hover {
        background: var(--backgroundA-3);
      }
    `)}
  }
`

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  ${Root}[aria-disabled='true'] & {
    color: var(--text-7);
  }
  ${Root}[aria-disabled='false'] & {
    color: var(--text-12);
  }
`

const Icon = styled.div`
  display: grid;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  ${Root}[aria-disabled='true'] & {
    color: var(--text-7);

    svg path {
      fill: var(--text-7);
    }
  }
  ${Root}[aria-disabled='false'] & {
    color: var(--text-12);
  }
`

const ArrowIcon = styled(ArrowRightIcon)`
  color: var(--text-9);
  cursor: pointer;

  ${Root}[aria-disabled='true'] & {
    color: var(--text-7);
  }
  ${Root}[aria-disabled='false'] & {
    color: var(--text-12);
  }
`
